import throttle from 'lodash/throttle';
// @ts-ignore
import { getCurrentActiveJourney, getJourneySettings } from 'conversional-environment/settings';
import { applyExternalData, publishHostUpdate } from './config';
var environment = process.env.REACT_APP_JOURNEY_ENV || 'development';
export function captureConversionalSettingsChange() {
    if (environment === 'development')
        return;
    var checkSettings = throttle(function () {
        var _a, _b;
        var currentJourney = getJourneySettings().journey;
        var activeJourney = getCurrentActiveJourney();
        var journeyLoaded = Boolean((_b = (_a = window.conversionalLauncher) === null || _a === void 0 ? void 0 : _a.journeyConfig) === null || _b === void 0 ? void 0 : _b.configuration);
        if (!activeJourney && !currentJourney && journeyLoaded)
            return window.conversionalLauncher.destroy();
        // Nothing loaded and nothing marked as loading: the initial bootstrap owns
        // this phase (runLauncher waits for the config element itself). A reload
        // here would only start a second bootstrap racing the first.
        if (!activeJourney && !journeyLoaded)
            return;
        if (activeJourney !== currentJourney) {
            window.conversionalLauncher.reload();
        }
    }, 1000);
    var observer = new MutationObserver(function () {
        checkSettings();
    });
    observer.observe(document.body, {
        childList: true,
        subtree: true,
        // Host scripts switch journeys by rewriting data-journey in place, which is
        // not a childList mutation.
        attributes: true,
        attributeFilter: ['data-journey'],
    });
    return function () {
        observer.disconnect();
        checkSettings.cancel();
    };
}
var HOST_URL_EVENTS = ['popstate', 'hashchange'];
var PATCHED_FLAG = '__conversionalUrlPatched';
var hostUrlObserverAttached = false;
/**
 * SPA/PWA host pages change the URL without reloading, so the href and query
 * params captured while bootstrapping go stale and the widget keeps rendering
 * for the page it was first loaded on. Re-publish the host data on every
 * navigation instead of reloading the launcher, which would destroy the
 * iframes and any journey in progress.
 */
export function captureHostUrlChange() {
    // Escape hatch for host pages where re-reading on navigation misbehaves:
    // set it before the bundle loads and the widget keeps its first-load data.
    if (window.conversionalDisableSpa)
        return;
    if (hostUrlObserverAttached)
        return;
    hostUrlObserverAttached = true;
    var lastHref = window.location.href;
    // Trailing-only: host scripts (custom integrations) react to the same
    // navigation and may write the host data objects in a timeout. Firing
    // on the leading edge would publish their previous page's data.
    var onUrlChange = throttle(function () {
        if (window.location.href === lastHref)
            return;
        lastHref = window.location.href;
        publishHostUpdate();
    }, 300, { leading: false, trailing: true });
    var patchedMethods = [];
    // History methods fire no event of their own, so they have to be wrapped.
    var patchHistoryMethod = function (method) {
        var _a;
        var original = (_a = window.history) === null || _a === void 0 ? void 0 : _a[method];
        if (typeof original !== 'function' || original[PATCHED_FLAG])
            return;
        var patched = function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            var result = original.apply(window.history, args);
            onUrlChange();
            return result;
        };
        patched[PATCHED_FLAG] = true;
        window.history[method] = patched;
        patchedMethods.push([method, original]);
    };
    patchHistoryMethod('pushState');
    patchHistoryMethod('replaceState');
    HOST_URL_EVENTS.forEach(function (event) { return window.addEventListener(event, onUrlChange); });
    return function () {
        HOST_URL_EVENTS.forEach(function (event) { return window.removeEventListener(event, onUrlChange); });
        patchedMethods.forEach(function (_a) {
            var method = _a[0], original = _a[1];
            window.history[method] = original;
        });
        onUrlChange.cancel();
        hostUrlObserverAttached = false;
    };
}
var widgetWriteWatcherAttached = false;
// Both host-owned data objects (see getHostWidget in config.ts).
var HOST_DATA_KEYS = ['conversionalWidget', 'conversionalIntegration'];
/**
 * Some host integrations write the host data objects asynchronously and at
 * unpredictable timing — a tag fired by a tag manager in particular commonly
 * takes seconds, not milliseconds, to run after page load, varying with
 * container size, trigger conditions, and consent gating. This iframe's own
 * first read of them (applyExternalData, in config.ts) can easily happen
 * before that write lands, and nothing re-reads them afterwards unless the
 * host later navigates (captureHostUrlChange above) — so a bookables filter
 * written late is otherwise dropped for the rest of the page view and the
 * widget falls back to its unfiltered default content. Rather than guess at a
 * fixed retry window (which a slow enough host write can still outrun), watch
 * the properties themselves: any assignment to window.conversionalWidget or
 * window.conversionalIntegration, whenever it happens, republishes
 * immediately — no timing assumption required.
 */
export function captureConversionalWidgetWrites() {
    if (widgetWriteWatcherAttached)
        return;
    widgetWriteWatcherAttached = true;
    var restores = [];
    HOST_DATA_KEYS.forEach(function (key) {
        var value = window[key];
        try {
            Object.defineProperty(window, key, {
                configurable: true,
                enumerable: true,
                get: function () {
                    return value;
                },
                set: function (next) {
                    value = next;
                    applyExternalData();
                },
            });
        }
        catch (e) {
            // A host page that already made this property non-configurable is left
            // as-is; config.ts's own short retry window is the fallback for that
            // case.
            return;
        }
        restores.push(function () {
            delete window[key];
            window[key] = value;
        });
    });
    if (!restores.length) {
        widgetWriteWatcherAttached = false;
        return undefined;
    }
    return function () {
        restores.forEach(function (restore) { return restore(); });
        widgetWriteWatcherAttached = false;
    };
}
